import { Component } from '@angular/core';
import { DataServiceService } from '../services/data-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Customers, Drivers, Orders } from '../interfaces';
import { TabStateService } from '../services/tab-state.service';


@Component({
  selector: 'app-drivers-schedule',
  templateUrl: './drivers-schedule.component.html',
  styleUrl: './drivers-schedule.component.scss'
})
export class DriversScheduleComponent {
readonly activeTab$;

  constructor(private dataServiceService: DataServiceService,
     private route: ActivatedRoute,
      private router: Router,
      private tabStateService: TabStateService) {
        this.activeTab$ = tabStateService.activeTab$;
      }

  dispatchOrders: any = [];
  lastSevenDays: Date[] = [];
  driverId: number | undefined;
  drivers: Drivers[] = []
  drivername: string = '';
  today = new Date();
  orders: Orders[] = [];
  selectedOrderId: number | undefined;
  customers: Customers[] = [];



  ngOnInit() {
    this.route.params.subscribe(params => {
      this.driverId = +params['driverId']; // Convert string to number using '+'
      this.loadLastSevenDays(this.today);
      this.loadDriverDetails();
    });

    this.dataServiceService.getAllDrivers().subscribe(data => {
      this.drivers = data
    })

    this.dataServiceService.getOrders().subscribe(data => {
      this.orders = data;
    })

    this.dataServiceService.getAllCustomers().subscribe(data => {
      this.customers = data;
    })
  }

  loadDriverDetails() {
    this.dataServiceService.getAllDrivers().subscribe({
      next: (drivers: any[]) => {
        const driver = drivers.find(d => d.driver_id === this.driverId);
        if (driver) {
          this.drivername = `${driver.first_name} ${driver.last_name}`;
        } else {
          console.error(`Driver not found for ID: ${this.driverId}`);
        }
      },
      error: (error) => {
        console.error('Error loading drivers:', error);
      }
    });
  }


  loadLastSevenDays(date: Date) {
    const endDate = new Date(date);
    const startDate = new Date(date);
    startDate.setDate(date.getDate() - 6);
    
    const formattedStartDate = this.formatDate(startDate);
    const formattedEndDate = this.formatDate(endDate);
  
    if (!this.driverId) {
      console.warn('No driverId provided, returning early');
      return;
    }
  
    this.dataServiceService.getDispatchOrdersForDateRange(
      this.driverId,
      formattedStartDate,
      formattedEndDate
    ).subscribe({
      next: (dispatchData: any[]) => { // Note: API returns an array
        // Generate the seven days
        this.lastSevenDays = Array.from({ length: 7 }, (_, i) => {
          const day = new Date(startDate);
          day.setDate(startDate.getDate() + i);
          return day;
        });
  
        // Initialize dispatchOrders object
        this.dispatchOrders = {};
        
        // Process each day
        this.lastSevenDays.forEach(day => {
          const formattedDate = this.formatDate(day);
          
          // Find dispatch for this date from the API response
          const dayDispatch = dispatchData.find(dispatch => 
            dispatch.dailyDispatch.date.split('T')[0] === formattedDate
          );
          
          // Store the dispatch orders for this date, or empty array if none exist
          this.dispatchOrders[formattedDate] = dayDispatch ? 
            dayDispatch.dispatchOrders : [];
        });
      },
      error: (error) => {
        console.error('Error loading dispatch orders:', error);
      }
    });
  }

  previousDay() {
    // Shift the entire date range back by 1 day
    this.lastSevenDays = this.lastSevenDays.map(date => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });

    // Reload orders for the new date range
    this.loadLastSevenDays(this.lastSevenDays[6]); // Pass the last day of the new range
  }

  nextDay() {
    // Shift the entire date range forward by 1 day
    this.lastSevenDays = this.lastSevenDays.map(date => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });

    // Reload orders for the new date range
    this.loadLastSevenDays(this.lastSevenDays[6]); // Pass the last day of the new range
  }

  getTotalOrdersForDay(date: Date): number {
    const formattedDate = this.formatDate(date);
    return this.dispatchOrders[formattedDate]?.length || 0;
  }

  getWeeklyTotal(): number {
    return this.lastSevenDays.reduce((total, date) => {
      const formattedDate = this.formatDate(date);
      return total + (this.dispatchOrders[formattedDate]?.length || 0);
    }, 0);
  }


  getDeliveredOrdersForDay(date: Date): number {
    const formattedDate = this.formatDate(date);
    return this.dispatchOrders[formattedDate]?.filter(
      (order: any) => order.load_status === 'Delivered'
    ).length || 0;
  }

  moreOrderInfo(orderId: number) {
    this.selectedOrderId = orderId
    const orderIndex = this.orders.sort((a, b) => b.order_id - a.order_id).findIndex(order => order.order_id === this.selectedOrderId);
        const pageIndex = Math.floor(orderIndex / 10);
        this.router.navigate(['/orders'], { 
          queryParams: { 
            orderId: this.selectedOrderId, 
            pageIndex: pageIndex,
            openOrder: true,
            expand: true
          }
        });
  }

  isToday(day: Date): boolean {
    const today = new Date();
    return day.getDate() === today.getDate() &&
           day.getMonth() === today.getMonth() &&
           day.getFullYear() === today.getFullYear();
  }

  // Helper method to get orders for a specific day
  getOrdersForDay(date: Date): any[] {
    const formattedDate = this.formatDate(date);
    const orders = this.dispatchOrders[formattedDate] || [];
    
    // Sort orders by priority to ensure consistent display order
    return [...orders].sort((a, b) => {
      const priorityA = parseInt(a.priority) || 0;
      const priorityB = parseInt(b.priority) || 0;
      return priorityA - priorityB;
    });
  }

  // Helper function to format date as YYYY-MM-DD
  private formatDate(date: Date): string {
    const centralTimeOffset = -6 * 60; // Central Time is UTC-6
    const centralDate = new Date(date.getTime() + centralTimeOffset * 60 * 1000);
    return centralDate.toISOString().split('T')[0];
  }

  getCustomerName(customerId: number): string {
    const customer = this.customers.find(c => c.customer_id === customerId);
    return customer ? `${customer.name.charAt(0).toUpperCase() + customer.name.slice(1).toLowerCase()}` : 'Unknown';
  }
}
